import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, Trash2, X, ThumbsUp, Edit, Share, Pin } from 'lucide-react';
import { renderEmojiWithFallback } from '../utils/emojiUtils';

const reactionItems = [
  { icon: renderEmojiWithFallback('👍') },
  { icon: renderEmojiWithFallback('🤝') },
  { icon: renderEmojiWithFallback('🙏') },
  { icon: renderEmojiWithFallback('👏') },
  { icon: renderEmojiWithFallback('🙌') },
  { icon: renderEmojiWithFallback('👎') },
  { icon: renderEmojiWithFallback('🚀') },
  { icon: renderEmojiWithFallback('🔥') },
  { icon: renderEmojiWithFallback('❤️') },
  { icon: renderEmojiWithFallback('😂') },
  { icon: renderEmojiWithFallback('😡') },
  { icon: renderEmojiWithFallback('😢') },
];

interface MessageMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onReply: () => void;
  onReact: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onForward: () => void;
  onReactResponse: (icon: string) => void;
  setSelectedMessage: React.Dispatch<React.SetStateAction<string | null>>;
  // Elemento da mensagem clicada (o menu é ancorado ao lado dele)
  anchorEl: HTMLElement | null;
  // Área visível do chat: o menu nunca sai desses limites
  boundaryEl: HTMLElement | null;
  reactItems: boolean;
  messageId: string;
  isOwnMessage: boolean;
  onPin: () => void;
  isPinned: boolean;
  onMention: () => void;
  isGroup: boolean;
  /** Groups API da Meta não permite editar/apagar mensagens de grupo */
  hideEdit?: boolean;
  hideDelete?: boolean;
}

const MENU_WIDTH = 180;
const MENU_GAP = 12; // distância entre a mensagem e o menu
const EDGE_MARGIN = 8; // margem mínima em relação às bordas da área visível

const MessageMenu: React.FC<MessageMenuProps> = ({
  isOpen,
  onClose,
  onReply,
  onReact,
  onEdit,
  onDelete,
  onForward,
  onReactResponse,
  setSelectedMessage,
  anchorEl,
  boundaryEl,
  reactItems,
  messageId,
  isOwnMessage,
  onPin,
  isPinned,
  onMention,
  isGroup,
  hideEdit = false,
  hideDelete = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const lastAnchorRectRef = useRef<DOMRect | null>(null);
  const [resolvedStyle, setResolvedStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  // Fechar o menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setSelectedMessage(null);
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Posiciona o menu (fixed, em coordenadas da janela) ao lado da mensagem,
  // usando a altura real do menu e mantendo-o sempre dentro da área visível do chat
  const updatePosition = useCallback(() => {
    const menu = menuRef.current;
    if (!menu || !anchorEl) return;

    const anchorRect = anchorEl.isConnected
      ? anchorEl.getBoundingClientRect()
      : lastAnchorRectRef.current;
    if (!anchorRect) return;
    lastAnchorRectRef.current = anchorRect;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const boundary = boundaryEl?.getBoundingClientRect();
    const minTop = Math.max(EDGE_MARGIN, (boundary ? boundary.top : 0) + EDGE_MARGIN);
    const maxBottom = Math.min(vh - EDGE_MARGIN, (boundary ? boundary.bottom : vh) - EDGE_MARGIN);
    const minLeft = Math.max(EDGE_MARGIN, (boundary ? boundary.left : 0) + EDGE_MARGIN);
    const maxRight = Math.min(vw - EDGE_MARGIN, (boundary ? boundary.right : vw) - EDGE_MARGIN);

    // Se o menu for mais alto que a área disponível, limita a altura e habilita rolagem
    const availableHeight = Math.max(0, maxBottom - minTop);
    const menuHeight = Math.min(menu.scrollHeight, availableHeight);
    const menuWidth = Math.max(menu.offsetWidth, MENU_WIDTH);

    // Horizontal: mensagem própria → menu à esquerda; do cliente → à direita.
    // Se não couber no lado preferido, tenta o outro; em último caso, encosta na borda.
    const leftSide = anchorRect.left - MENU_GAP - menuWidth;
    const rightSide = anchorRect.right + MENU_GAP;
    const fitsLeft = leftSide >= minLeft;
    const fitsRight = rightSide + menuWidth <= maxRight;
    let left: number;
    if (isOwnMessage) {
      left = fitsLeft ? leftSide : fitsRight ? rightSide : leftSide;
    } else {
      left = fitsRight ? rightSide : fitsLeft ? leftSide : rightSide;
    }
    left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxRight - menuWidth));

    // Vertical: alinhado ao topo da mensagem, mas sempre inteiro dentro da área visível
    const top = Math.min(Math.max(anchorRect.top, minTop), Math.max(minTop, maxBottom - menuHeight));

    setResolvedStyle({
      top: `${top}px`,
      left: `${left}px`,
      maxHeight: `${availableHeight}px`,
      visibility: 'visible',
    });
  }, [anchorEl, boundaryEl, isOwnMessage]);

  // Recalcula ao abrir e sempre que o conteúdo do menu muda de altura
  useLayoutEffect(() => {
    if (!isOpen) {
      setResolvedStyle({ visibility: 'hidden' });
      lastAnchorRectRef.current = null;
      return;
    }
    updatePosition();
  }, [isOpen, updatePosition, reactItems, isGroup]);

  // Acompanha a mensagem ao rolar o chat ou redimensionar a janela
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  if (!isOpen) return null;

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 1000,
    minWidth: `${MENU_WIDTH}px`,
    overflowY: 'auto',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.18)',
    visibility: 'hidden',
    ...resolvedStyle,
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.2s',
  };

  const iconStyle: React.CSSProperties = {
    marginRight: '10px',
    width: '16px',
    height: '16px',
  };

  // Renderizado no body para não ser cortado pelo overflow da área de mensagens
  return createPortal(
    <div ref={menuRef} style={containerStyle}>
      <div style={{
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        fontWeight: 500,
        fontSize: '14px',
      }} className="dark:bg-gray-800 dark:text-white">
        Mensagem
        <button
          onClick={() => { setSelectedMessage(null); onClose(); }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={16} color="#667" />
        </button>
      </div>

      <div
        style={{ ...menuItemStyle, padding: '10px 16px', transition: 'background-color 0.2s ease' }}
        className="dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
        onClick={onReply}
      >
        <MessageSquare size={16} style={iconStyle} />
        <span>Responder</span>
      </div>

      {isOwnMessage && !hideEdit && (
        <div
          style={{ ...menuItemStyle, padding: '10px 16px', transition: 'background-color 0.2s ease' }}
          className="dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
          onClick={onEdit}
        >
          <Edit size={16} style={iconStyle} />
          <span>Editar</span>
        </div>
      )}

      <div
        style={{ ...menuItemStyle, padding: '10px 16px', transition: 'background-color 0.2s ease' }}
        className={`dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 ${reactItems ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
        onClick={onReact}
      >
        <ThumbsUp size={16} style={iconStyle} />
        <span>Reagir</span>
      </div>

      {reactItems && (
        <div className="dark:bg-gray-800">
          <div style={{ overflowX: 'auto', maxWidth: `${MENU_WIDTH}px` }} className="flex">
            {reactionItems.map((item, i) => (
              <div
                key={i}
                style={{ display: 'flex', alignItems: 'center', padding: '10px', cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                className="hover:bg-gray-200 dark:hover:bg-gray-700"
                onClick={() => onReactResponse(item.icon)}
              >
                <span style={{ fontSize: '18px' }}>{item.icon}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{ ...menuItemStyle, padding: '10px 16px', transition: 'background-color 0.2s ease' }}
        className="dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
        onClick={onForward}
      >
        <Share size={16} style={iconStyle} />
        <span>Encaminhar</span>
      </div>

      <div
        style={{ ...menuItemStyle, padding: '10px 16px', transition: 'background-color 0.2s ease' }}
        className="dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
        onClick={onPin}
      >
        <Pin size={16} style={{ ...iconStyle, color: isPinned ? '#e74c3c' : 'inherit' }} />
        <span>{isPinned ? 'Desfixar' : 'Fixar'}</span>
      </div>

      {isGroup && onMention && (
        <div
          style={{ ...menuItemStyle, padding: '10px 16px', transition: 'background-color 0.2s ease' }}
          className="dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
          onClick={() => {
            onMention();
            onClose();
          }}
        >
          <span style={{ ...iconStyle, fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>@</span>
          <span>Marcar Alguém</span>
        </div>
      )}

      {isOwnMessage && !hideDelete && (
        <div
          style={{ ...menuItemStyle, padding: '10px 16px', color: '#e74c3c', transition: 'background-color 0.2s ease' }}
          className="dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
          onClick={onDelete}
        >
          <Trash2 size={16} style={{ ...iconStyle, color: '#e74c3c' }} />
          <span>Apagar</span>
        </div>
      )}
    </div>,
    document.body
  );
};

export default MessageMenu;